/* eslint-disable @typescript-eslint/no-explicit-any -- DIRECT-239681 */

import { AnyFn } from '@direct-frontend/stdlib';

import type {
    MaybePromise,
    RequestHeaders,
    RequestInitOptions,
    RequestMethod,
    RequestOptions,
    ResolvedRequestOptions,
    RetryOption,
    RetryOptions,
} from '../../types';
import { appendToBaseUrl, normalizeMethod } from '../../utils';
import { isPlainObject } from './isPlainObject';
import { defaultValidateStatus } from './defaultValidateStatus';

const defaultRetryStatusCodes = [408, 413, 500, 502, 503, 504, 521, 522, 524];
const defaultRetryMethods: RequestMethod[] = ['GET', 'PUT', 'HEAD', 'DELETE', 'OPTIONS', 'TRACE'];
const defaultJsonContentTypeHeaders = ['application/json'];

export function resolveOptions<Result, PluginOptions>(
    options: RequestOptions<Result, PluginOptions>,
    initOptions: RequestInitOptions
): ResolvedRequestOptions<Result, PluginOptions> {
    const {
        headers: initOptionsHeaders,
        plugins: pluginsGetters = [],
        retry: initRetryOptions,
        baseUrl: baseUrlGetter,
        responseType: initResponseType,
        validateStatus: initOptionsValidateStatus,
        jsonContentTypeHeaders = defaultJsonContentTypeHeaders,
        ...restInitOptions
    } = initOptions;
    const {
        url: urlGetter,
        body = undefined,
        headers: optionsHeaders = undefined,
        method = 'GET',
        transformOptions = undefined,
        transformResponse = undefined,
        transformResult = undefined,
        onEnd = undefined,
        validateStatus = undefined,
        retry,
        responseType,
        ...rest
    } = options;

    const baseUrl = resolveGetter(baseUrlGetter);
    const url = resolveGetter(urlGetter);
    const plugins = pluginsGetters.map(resolveGetter);

    const headers = resolveHeaders({
        ...resolveGetter(initOptionsHeaders),
        ...optionsHeaders,
    });

    if (!headers['content-type'] && isSerializableToJson(body)) {
        headers['content-type'] = jsonContentTypeHeaders[0];
    }

    return {
        ...restInitOptions,
        ...rest,
        body: resolveBody(headers, jsonContentTypeHeaders, body),
        headers,
        method,
        responseType: responseType ?? initResponseType ?? 'json',
        retry: resolveRetryOptions(retry ?? initRetryOptions),
        transformOptions: composeTransforms([...plugins.map(plugin => plugin?.transformOptions), transformOptions]),
        transformResponse: composeTransforms([...plugins.map(plugin => plugin?.transformResponse), transformResponse]),
        transformResult: composeTransforms([...plugins.map(plugin => plugin?.transformResult), transformResult]),
        onEnd: (...args) => [...plugins.map(plugin => plugin?.onEnd), onEnd].forEach(fn => fn?.(...args)),
        url: appendToBaseUrl(baseUrl ?? '', url),
        validateStatus: validateStatus ?? initOptionsValidateStatus ?? defaultValidateStatus,
    };
}

function resolveHeaders(headers: RequestHeaders): RequestHeaders {
    return Object.entries(headers).reduce<RequestHeaders>((normalizedHeaders, [header, value]) => {
        normalizedHeaders[header.toLowerCase()] = value;
        return normalizedHeaders;
    }, {});
}

function resolveBody(headers: RequestHeaders, jsonContentTypeHeaders: string[], body?: any) {
    if (isJsonContentType(headers, jsonContentTypeHeaders) && isSerializableToJson(body)) {
        return JSON.stringify(body);
    }

    return body;
}

function isSerializableToJson(body: any) {
    return (
        typeof body === 'object' && (isPlainObject(body) || Array.isArray(body) || typeof body.toJSON === 'function')
    );
}

function isJsonContentType(headers: RequestHeaders, jsonContentTypeHeaders: string[]) {
    const contentType = headers['content-type']?.toLowerCase();
    return contentType && jsonContentTypeHeaders.some(jsonContentType => contentType.startsWith(jsonContentType));
}

function resolveRetryOptions(options: RetryOption = {}): RetryOptions {
    const {
        calculateDelay = arg => arg.computedValue,
        errorCodes = [],
        limit = 0,
        methods = defaultRetryMethods,
        statusCodes = defaultRetryStatusCodes,
    }: Partial<RetryOptions> = typeof options === 'number' ? { limit: options } : options;

    return {
        calculateDelay,
        errorCodes,
        limit,
        methods: methods.map(normalizeMethod),
        statusCodes,
    };
}

function composeTransforms<TransformFn extends (...args: any[]) => MaybePromise<any>>(
    transforms: (TransformFn | undefined)[]
) {
    return async <Result = Awaited<ReturnType<TransformFn>>>(result: Result, ...restArgs: any[]): Promise<Result> => {
        for (let transform of transforms) {
            if (transform) {
                result = await transform(result, ...restArgs);
            }
        }

        return result;
    };
}

function resolveGetter<Value>(valueOrGetter: (() => Value) | Value): Value {
    return isFunction(valueOrGetter) ? valueOrGetter() : valueOrGetter;
}

// Без тайпгарда TS выводит неверный тип в условии:
// `x: (() => Value) | Value` после проверки `typeof x === 'function'` превращается в (Value & Function)
function isFunction(value: unknown): value is AnyFn {
    return typeof value === 'function';
}
